import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Loader2,
  Link2,
  SlidersHorizontal,
  ClipboardCheck,
  Search,
  X,
} from 'lucide-react';
import { TestService } from 'src/services/api/TestService';
import AppSelect from 'src/components/common/AppSelect';
import { useVendors } from 'src/services/api/queries/vendorQueries';

const SectionCard = ({ icon: Icon, title, subtitle, badge, children }) => (
  <div className="rounded-[10px] border border-border bg-card overflow-hidden">
    <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-border">
      <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-[15px] h-[15px]" strokeWidth={1.9} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-[650] text-foreground-strong leading-tight">
          {title}
        </div>
        {subtitle && (
          <div className="text-[12px] text-muted-foreground leading-snug">
            {subtitle}
          </div>
        )}
      </div>
      {badge}
    </div>
    <div className="p-[18px]">{children}</div>
  </div>
);

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[600] text-foreground mb-1.5"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const fieldBase =
  'w-full px-3 py-[9px] rounded-lg border border-input text-[13px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

const resolveTest = (t) => ({
  test_id: t?.test_id ?? t?.id ?? t?.test?.id,
  name:
    t?.test?.name ??
    t?.name ??
    t?.title ??
    t?.test?.title ??
    `Test ${t?.test_id ?? t?.id}`,
  external_test_ref: t?.external_test_ref ?? '',
});

export const NewTestGroup = ({
  onSubmit,
  onClose,
  initialData,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    vendor_id: initialData?.vendor_id ? String(initialData.vendor_id) : '',
  });

  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [selectedTestOptions, setSelectedTestOptions] = useState([]);
  const [testsWithRef, setTestsWithRef] = useState([]);

  // Picklist state
  const [availableTests, setAvailableTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [testsLoading, setTestsLoading] = useState(true);
  const [hasNext, setHasNext] = useState(false);
  const [testPage, setTestPage] = useState(1);

  const searchDebounceRef = useRef(null);

  const { data: vendorData } = useVendors(false);
  const vendorOptions = (vendorData?.data ?? vendorData ?? [])
    .filter((v) => v.is_active)
    .map((v) => ({ value: String(v.id), label: v.name }));

  const loadTests = useCallback(async (page, search) => {
    setTestsLoading(true);
    try {
      const res = await TestService.listAllTests(page, 50, search, 'ACTIVE');
      const items = res?.data?.items || [];
      if (page === 1) {
        setAvailableTests(items);
      } else {
        setAvailableTests((prev) => [...prev, ...items]);
      }
      setHasNext(res?.data?.pagination?.has_next || false);
      setTestPage(page);
    } catch (err) {
      console.error('Failed to load tests:', err);
    } finally {
      setTestsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTests(1, '');
  }, [loadTests]);

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setTestSearch(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => loadTests(1, val), 300);
  };

  // Load initial / edit data
  useEffect(() => {
    if (!initialData?.id) {
      if (initialData?.tests?.length) {
        const resolved = initialData.tests.map(resolveTest);
        setTestsWithRef(resolved);
        setSelectedTestOptions(
          resolved.map((t) => ({ label: t.name, value: String(t.test_id) }))
        );
      }
      return;
    }

    const fetchDetails = async () => {
      try {
        setFetchingDetails(true);
        const response = await TestService.getTestGroup(initialData.id);
        const payload = response?.data;
        const groupData =
          payload?.data && typeof payload.data === 'object'
            ? payload.data
            : payload;
        if (groupData && typeof groupData === 'object') {
          const tests = (groupData?.tests || []).map(resolveTest);
          setFormData((prev) => ({
            ...prev,
            name: groupData.name ?? prev.name,
            description: groupData.description ?? prev.description,
            is_active: groupData.is_active ?? prev.is_active,
            vendor_id: groupData.vendor_id
              ? String(groupData.vendor_id)
              : prev.vendor_id,
          }));
          setTestsWithRef(tests);
          setSelectedTestOptions(
            tests.map((t) => ({ label: t.name, value: String(t.test_id) }))
          );
        }
      } catch (error) {
        console.error('Failed to fetch test group details:', error);
      } finally {
        setFetchingDetails(false);
      }
    };

    fetchDetails();
  }, [initialData?.id]);

  const toggleTest = (test) => {
    const testId = String(test.id);
    const isSelected = selectedTestOptions.some((s) => s.value === testId);
    if (isSelected) {
      setSelectedTestOptions((prev) => prev.filter((s) => s.value !== testId));
      setTestsWithRef((prev) =>
        prev.filter((t) => String(t.test_id) !== testId)
      );
    } else {
      const name = test.name || test.title || '';
      setSelectedTestOptions((prev) => [
        ...prev,
        { label: name, value: testId },
      ]);
      setTestsWithRef((prev) => [
        ...prev,
        { test_id: test.id, name, external_test_ref: '' },
      ]);
    }
  };

  const deselectTest = (testId) => {
    setSelectedTestOptions((prev) => prev.filter((s) => s.value !== testId));
    setTestsWithRef((prev) => prev.filter((t) => String(t.test_id) !== testId));
  };

  const handleRefChange = (testId, value) => {
    setTestsWithRef((prev) =>
      prev.map((t) =>
        t.test_id === testId ? { ...t, external_test_ref: value } : t
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      is_active: formData.is_active,
      ...(formData.vendor_id && { vendor_id: Number(formData.vendor_id) }),
      tests: testsWithRef.map((t) => ({
        test_id: t.test_id,
        external_test_ref: t.external_test_ref,
      })),
    });
  };

  const isEditMode = Boolean(initialData?.id);
  const buttonText = isEditMode ? 'Update Group' : 'Create group';
  const loadingText = isEditMode ? 'Updating…' : 'Creating…';
  const isLoading = loading || fetchingDetails;
  const descLen = formData.description.length;

  const selectedIds = new Set(selectedTestOptions.map((s) => s.value));
  const unselectedTests = availableTests.filter(
    (t) => !selectedIds.has(String(t.id))
  );

  if (fetchingDetails) {
    return (
      <div className="space-y-[18px] min-h-[calc(100vh-3.4rem)]">
        <div className="rounded-[10px] border border-border bg-card p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle animate-pulse flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-[14px] bg-border rounded animate-pulse w-40" />
              <div className="h-3 bg-border rounded animate-pulse w-60" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-3 bg-border rounded animate-pulse w-24" />
            <div className="h-[38px] bg-border rounded-lg animate-pulse" />
            <div className="h-3 bg-border rounded animate-pulse w-28" />
            <div className="h-[70px] bg-border rounded-lg animate-pulse" />
            <div className="grid grid-cols-2 gap-[14px] mt-4">
              <div className="h-[38px] bg-border rounded-lg animate-pulse" />
              <div className="h-[38px] bg-border rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
        <div className="rounded-[10px] border border-border bg-card p-5">
          <div className="h-[200px] bg-border rounded-lg animate-pulse" />
        </div>
        <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-[13px]">Loading group details…</span>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-[18px] min-h-[calc(100vh-3.4rem)] pb-20"
    >
      {/* ── Group Details ── */}
      <SectionCard
        icon={SlidersHorizontal}
        title="Group details"
        subtitle="Name it, and say who runs it"
      >
        {/* Name */}
        <div className="mb-4">
          <FieldLabel htmlFor="gName" required>
            Group name
          </FieldLabel>
          <input
            id="gName"
            className={fieldBase}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Frontline Sales Screening"
            required
            maxLength={80}
            disabled={isLoading}
            autoComplete="off"
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <FieldLabel htmlFor="gDesc">Description</FieldLabel>
          <textarea
            id="gDesc"
            className={`${fieldBase} min-h-[80px] resize-y leading-[1.55]`}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Add a description…"
            maxLength={240}
            disabled={isLoading}
          />
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-[11.5px] text-muted-foreground">
              Optional. Helps recruiters pick the right group.
            </span>
            <span
              className={`font-mono text-[11px] ${
                descLen >= 240 ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              {descLen} / 240
            </span>
          </div>
        </div>

        {/* Grid: Vendor + Status */}
        <div className="grid grid-cols-2 gap-[14px]">
          <div>
            <FieldLabel htmlFor="gVendor" required>
              Vendor
            </FieldLabel>
            <AppSelect
              options={vendorOptions}
              value={formData.vendor_id || undefined}
              onValueChange={(v) => setFormData({ ...formData, vendor_id: v })}
              placeholder="Select a vendor…"
              disabled={isLoading}
              className="h-[38px]"
            />
          </div>
          <div>
            <FieldLabel>Status</FieldLabel>
            <div className="flex items-center gap-2.5 pt-1.5">
              <button
                type="button"
                role="switch"
                aria-label="Active status"
                aria-checked={formData.is_active}
                onClick={() =>
                  !isLoading &&
                  setFormData({ ...formData, is_active: !formData.is_active })
                }
                disabled={isLoading}
                className={`relative inline-flex h-[22px] w-[38px] cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                } ${formData.is_active ? 'bg-green-600' : 'bg-input'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform ${
                    formData.is_active ? 'translate-x-[16px]' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <div className="text-[13px] font-[550] text-foreground leading-tight">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </div>
                <div className="text-[11.5px] text-muted-foreground">
                  {formData.is_active
                    ? 'Visible when building a pipeline'
                    : 'Hidden from pipeline builders'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Tests ── */}
      <SectionCard
        icon={ClipboardCheck}
        title="Tests"
        subtitle="Pick the tests this group runs, in order"
        badge={
          <span className="ml-auto font-mono text-[11.5px] font-semibold text-primary bg-brand-bg-subtle px-2.5 py-0.5 rounded-[6px] flex-shrink-0">
            {selectedTestOptions.length} selected
          </span>
        }
      >
        {/* Selected pills */}
        {selectedTestOptions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedTestOptions.map((opt, i) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-[5px] rounded-[7px] text-[12px] font-semibold bg-brand-bg-subtle text-primary border border-primary/10"
              >
                <span className="font-mono text-[10px] font-bold opacity-70">
                  {i + 1}
                </span>
                {opt.label}
                <button
                  type="button"
                  className="w-[17px] h-[17px] rounded-[5px] bg-primary/15 text-primary hover:bg-destructive/15 hover:text-destructive flex items-center justify-center transition-colors"
                  onClick={() => deselectTest(opt.value)}
                  aria-label={`Remove ${opt.label}`}
                >
                  <X className="w-2.5 h-2.5" strokeWidth={3} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-muted-foreground mb-3">
            No tests selected yet.
          </p>
        )}

        {/* Search + Clear */}
        <div className="flex gap-2.5 items-center mb-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-muted-foreground"
              strokeWidth={2}
            />
            <input
              type="text"
              value={testSearch}
              onChange={handleSearchChange}
              placeholder="Filter tests…"
              className="w-full pl-[34px] pr-3 py-2 rounded-lg border border-input text-[12.5px] bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
            />
          </div>
          {selectedTestOptions.length > 0 && (
            <button
              type="button"
              className="px-3 py-[9px] rounded-lg border border-border text-[13px] font-[500] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-colors whitespace-nowrap"
              onClick={() => {
                setSelectedTestOptions([]);
                setTestsWithRef([]);
              }}
            >
              Clear selection
            </button>
          )}
        </div>

        {/* Picklist */}
        <div className="border border-border rounded-lg max-h-[280px] overflow-y-auto">
          {testsLoading ? (
            <div className="flex items-center justify-center py-8 text-[13px] text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Loading tests…
            </div>
          ) : (
            <>
              {/* In this group */}
              {selectedTestOptions.length > 0 && (
                <>
                  <div className="sticky top-0 z-10 px-3 py-[7px] text-[10.5px] uppercase tracking-[.1em] font-semibold text-muted-foreground bg-brand-bg-subtle border-b border-border">
                    In this group · {selectedTestOptions.length}
                  </div>
                  {selectedTestOptions.map((opt) => {
                    const full = availableTests.find(
                      (t) => String(t.id) === opt.value
                    );
                    return (
                      <label
                        key={opt.value}
                        htmlFor={`sel-test-${opt.value}`}
                        className="flex items-center gap-3 px-3 py-2.5 border-b border-border cursor-pointer bg-brand-bg-subtle/40 hover:bg-brand-bg-subtle"
                      >
                        <input
                          id={`sel-test-${opt.value}`}
                          type="checkbox"
                          checked
                          onChange={() => deselectTest(opt.value)}
                          className="w-4 h-4 accent-primary flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-[550] text-foreground truncate">
                            {opt.label}
                          </div>
                          {full?.description && (
                            <div className="text-[11.5px] text-muted-foreground truncate">
                              {full.description}
                            </div>
                          )}
                        </div>
                        {full?.max_score != null && (
                          <span className="font-mono text-[12px] font-semibold text-muted-foreground flex-shrink-0">
                            {full.max_score}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </>
              )}

              {/* Available tests */}
              {unselectedTests.length > 0 && (
                <>
                  <div className="sticky top-0 z-10 px-3 py-[7px] text-[10.5px] uppercase tracking-[.1em] font-semibold text-muted-foreground bg-brand-bg-subtle border-b border-border">
                    Available tests
                  </div>
                  {unselectedTests.map((test) => (
                    <label
                      key={test.id}
                      htmlFor={`avail-test-${test.id}`}
                      className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-b-0 cursor-pointer hover:bg-brand-bg-subtle"
                    >
                      <input
                        id={`avail-test-${test.id}`}
                        type="checkbox"
                        checked={false}
                        onChange={() => toggleTest(test)}
                        className="w-4 h-4 accent-primary flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-[550] text-foreground truncate">
                          {test.name || test.title}
                        </div>
                        {test.description && (
                          <div className="text-[11.5px] text-muted-foreground truncate">
                            {test.description}
                          </div>
                        )}
                      </div>
                      {test.max_score != null && (
                        <span className="font-mono text-[12px] font-semibold text-muted-foreground flex-shrink-0">
                          {test.max_score}
                        </span>
                      )}
                    </label>
                  ))}
                </>
              )}

              {selectedTestOptions.length === 0 &&
                unselectedTests.length === 0 && (
                  <p className="text-center text-[12.5px] text-muted-foreground py-6">
                    No tests match that filter.
                  </p>
                )}
            </>
          )}
        </div>

        {hasNext && !testsLoading && (
          <button
            type="button"
            className="mt-2 text-[12.5px] text-primary hover:underline"
            onClick={() => loadTests(testPage + 1, testSearch)}
          >
            Load more…
          </button>
        )}
      </SectionCard>

      {/* ── External References ── */}
      {testsWithRef.length > 0 && (
        <SectionCard
          icon={Link2}
          title="External test references"
          subtitle="Map each test to the vendor's own test ID"
        >
          <div className="space-y-2">
            {testsWithRef.map((test, index) => (
              <div key={test.test_id} className="flex items-center gap-2.5">
                <div className="w-[24px] h-[24px] rounded-[6px] bg-brand-bg-subtle text-primary font-mono text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <span
                  className="text-[13px] font-[500] text-foreground w-[180px] flex-shrink-0 truncate"
                  title={test.name}
                >
                  {test.name}
                </span>
                <input
                  className="flex-1 min-w-0 px-2.5 py-2 rounded-[7px] border border-input font-mono text-[12px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50"
                  value={test.external_test_ref}
                  onChange={(e) =>
                    handleRefChange(test.test_id, e.target.value)
                  }
                  placeholder="Vendor test ID"
                  disabled={isLoading}
                />
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Sticky Commit Bar ── */}
      <div className="sticky bottom-0 z-20 flex items-center justify-end gap-2 py-[14px] px-5 mt-6 border-t border-border bg-mutable/50 backdrop-blur-[2px]">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={isLoading || !formData.name.trim()}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </form>
  );
};
